import useMediaQuery from "@mui/material/useMediaQuery";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InView } from "react-intersection-observer";
import { useDispatch } from "react-redux";
import { sendTrackingEvent } from "../../../../helpers/tracking_management";
import useTriggerSnackbar from "../../../../hooks/useTriggerSnackbar";
import {
  bookmarkJob,
  getJobSimilarJobsV1,
  unBookmarkJob,
  updateSelectedJob,
} from "../../../../redux/actions/job_action";
import {
  updateShowSignInModalStatus,
  updateSignUpOrigin,
} from "../../../../redux/actions/navbar_action";
import {
  checkAuthentication,
  getUser,
} from "../../../../redux/actions/user_action";
import * as jobTypes from "../../../../redux/types/job_type";
import {
  CompanyNameRow,
  CompanyVerifiedBadge,
} from "../../AccordionJobCard/styles";
import {
  BookmarkIconStyled,
  FilledBookmarkIconStyled,
  GridStyled,
  HotJobTag,
  IconButtonStyled,
  ImageStyled,
  JobTypeTag,
  LinkStyled,
  OutlinedButton,
  SectionTitle,
  SimilarJobCompanyName,
  SimilarJobContainer,
  SimilarJobTitle,
  SparklesIconStyled,
  StatusBadgeContainer,
  StatusClockIcon,
} from "./styles";

export default function SingleJobSimilarJobs(props) {
  const { job, setCheckSimilarJob } = props || {};
  const { id: jobId } = job || {};

  const belowTablet = useMediaQuery("(max-width: 767px)");
  const user = getUser();
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useTranslation();

  const [showSimilarJobs, setShowSimilarJobs] = useState(false);
  const [similarJobs, setSimilarJobs] = useState([]);

  async function getSimilarJobs() {
    const similarJobRes = await dispatch(getJobSimilarJobsV1({ jobId: jobId }));

    if (
      similarJobRes.type === jobTypes.FETCH_SIMILAR_JOBS_UNDER_JOB_PAGE_SUCCEED
    ) {
      setSimilarJobs(similarJobRes.payload ?? []);
      setCheckSimilarJob(similarJobRes.payload ?? []);
    }
  }

  const jobTitle = job?.title;
  const processedJobTitle = jobTitle
    .replaceAll(/[(){}[\]"`~,./&%$*!@-]/g, "")
    .trim()
    .replaceAll(" ", "-");

  async function handleGoToJobsPage() {
    sendTrackingEvent({
      event: "CE_click-see-more-similar-jobs-ijp",
      "company-id": job?.company?.id,
      "job-id": job?.id,
    });
  }

  // This component needs to CSR because we don't want irrelavant job title keyword to be crawled
  useEffect(() => {
    getSimilarJobs();
    setShowSimilarJobs(true);
  }, [jobId]);

  // show the component / check if similarJobs ARRAY is not empty --> ONLY WHEN COMPONENT IS MOUNTED TO DOM
  return showSimilarJobs === true ? (
    Array.isArray(similarJobs) && similarJobs.length > 0 ? (
      <Fragment>
        <SectionTitle variant="h1">{t("jobs.similar_jobs.title")}</SectionTitle>
        <GridStyled
          display={belowTablet ? "flex" : "grid"}
          gridTemplateColumns={"repeat(2, 1fr)"}
          columnGap={"17px"}
          rowGap={"17px"}
          sx={{
            overflowX: belowTablet ? "scroll" : null,
          }}
          marginBottom={"30px"}
        >
          {similarJobs.map((similarJob, index) => (
            <SimilarJobCard
              key={`similar-job-${similarJob.id}`}
              job={similarJob}
              index={index}
              belowTablet={belowTablet}
            />
          ))}
        </GridStyled>
        <GridStyled container justifyContent={"center"} marginBottom={"50px"}>
          <LinkStyled
            href={`/${processedJobTitle}-jobs`}
            onClick={handleGoToJobsPage}
          >
            <OutlinedButton variant="outlined">
              {t("jobs.similar_jobs.view_more")}
            </OutlinedButton>
          </LinkStyled>
        </GridStyled>
      </Fragment>
    ) : null
  ) : null;
}

function SimilarJobCard(props) {
  const { job, belowTablet } = props || {};
  const { id, title, boosted, jobType, company, slug, bookmark } = job || {};
  const { name, logo } = company;
  const { t } = useTranslation();

  const getJobTypeLabel = (jobTypeValue) => {
    if (!jobTypeValue) return "";
    const key = jobTypeValue
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companydetail.job_type_labels.${key}`, {
      defaultValue: jobTypeValue,
    });
  };

  const getStatusBadge = (employerStatus) => {
    switch (employerStatus) {
      case "Expiring Soon":
        return {
          text: "Expiring soon",
          color: "#F75443",
          icon: <StatusClockIcon />,
        };
      case "Actively Reviewing":
        return {
          text: "Actively reviewing",
          color: "#6AA84F",
          icon: null,
        };
      case "Be an Early Applicant":
        return {
          text: "Be an early applicant",
          color: "#512ACC",
          icon: null,
        };
      default:
        return null;
    }
  };

  const statusBadge = getStatusBadge(job?.employerStatus);

  const isCompanyVerified =
    company?.verified === true || name === "Hiredly" || name === "Hiredly X";

  function triggerTrackClick() {
    sendTrackingEvent({
      event: "CE_click-job-similar-job-ijp",
      "job-id": id,
      "job-title": title,
    });
    Cookies.set("click-source", "job_detail_similar_jobs");
  }

  function trackImpressionInView(job, index) {
    // POSTHOG JOB IMPRESSION TRACKING

    sendTrackingEvent({
      event: "CE_job-impression-individual-job-page",
      "job-id": job?.id,
      origin: "individual_job_page",
    });
  }

  return (
    <LinkStyled
      href={`/jobs/${slug}?origin=similar_jobs`}
      onClick={triggerTrackClick}
    >
      <SimilarJobContainer
        item
        container
        flexDirection={"column"}
        alignItems={"flex-start"}
        justifyContent={"space-between"}
      >
        <GridStyled container>
          <GridStyled
            container
            justifyContent={"space-between"}
            marginBottom={"10px"}
            gridTemplateColumns={12}
            flexWrap={"nowrap"}
          >
            <GridStyled item xs={boosted ? 9 : 12}>
              <InView
                key={`${title}-inview-observer`}
                onChange={(inView) =>
                  inView === true &&
                  trackImpressionInView(props.job, props.index)
                }
              >
                <SimilarJobTitle
                  variant="h1"
                  aria-label="similar-job-job-title"
                >
                  {title}
                </SimilarJobTitle>
              </InView>
            </GridStyled>
            {boosted ? (
              <GridStyled item xs={3} container justifyContent={"flex-end"}>
                <HotJobTag>
                  <SparklesIconStyled /> HOT
                </HotJobTag>
              </GridStyled>
            ) : null}
          </GridStyled>
          <GridStyled
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto",
              alignItems: "center",
              columnGap: "8px",
              width: "100%",
              marginBottom: "20px",
            }}
          >
            <JobTypeTag
              className="job-type-tag"
              sx={{ marginBottom: 0, justifySelf: "flex-start" }}
            >
              {getJobTypeLabel(jobType)}
            </JobTypeTag>
            {belowTablet && statusBadge && (
              <StatusBadgeContainer $color={statusBadge.color}>
                {statusBadge.icon}
                {statusBadge.text}
              </StatusBadgeContainer>
            )}
          </GridStyled>
        </GridStyled>
        <GridStyled
          sx={{
            display: "grid",
            gridTemplateColumns: belowTablet
              ? "minmax(0, 1fr) auto"
              : "minmax(0, 1fr) 190px",
            alignItems: "center",
            columnGap: "12px",
            width: "100%",
          }}
        >
          <GridStyled
            sx={{
              display: "grid",
              gridTemplateColumns: logo
                ? "30px minmax(0, 1fr)"
                : "minmax(0, 1fr)",
              alignItems: "center",
              columnGap: "8px",
              minWidth: 0,
            }}
          >
            {logo ? (
              <ImageStyled src={logo} alt={`${name}-logo-image`} />
            ) : null}
            <CompanyNameRow sx={{ minWidth: 0, overflow: "hidden" }}>
              <SimilarJobCompanyName
                aria-label="similar-job-company-name"
                variant="body1"
                sx={{ flex: 1, minWidth: 0, width: "auto" }}
              >
                {name}
              </SimilarJobCompanyName>
              {isCompanyVerified && (
                <CompanyVerifiedBadge aria-label="Verified company" />
              )}
            </CompanyNameRow>
          </GridStyled>

          <GridStyled
            container
            item
            alignItems={"center"}
            justifyContent={"flex-end"}
            flexWrap={"nowrap"}
            columnGap={"8px"}
            sx={{ minWidth: 0 }}
          >
            {!belowTablet && statusBadge && (
              <StatusBadgeContainer $color={statusBadge.color}>
                {statusBadge.icon}
                {statusBadge.text}
              </StatusBadgeContainer>
            )}

            <SaveSimilarJobButton id={id} job={props.job} bookmark={bookmark} />
          </GridStyled>
        </GridStyled>
      </SimilarJobContainer>
    </LinkStyled>
  );
}

function SaveSimilarJobButton(props) {
  const { id, job, bookmark } = props || {};

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [savingJob, setSavingJob] = useState(false);
  const [savedJob, setSavedJob] = useState(bookmark ? bookmark : false);

  const [triggerSnackbarFunc] = useTriggerSnackbar();

  async function handleBookmarkJob(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!savingJob) setSavingJob(true);
    const auth = checkAuthentication();

    if (!auth) {
      setSavingJob(false);
      return dispatch(updateSelectedJob(job)).then(() => {
        dispatch(updateSignUpOrigin("save_job")).then(() => {
          dispatch(updateShowSignInModalStatus(true));
        });
      });
    }

    if (savedJob) {
      let params = {
        jobId: id,
        canCancel: false,
        appliedDialog: false,
        origin: "similar-job-ijp",
      };
      const unBookmarkRes = await dispatch(unBookmarkJob(params));
      setSavingJob(false);
      setSavedJob(unBookmarkRes?.job?.bookmark);
      triggerSnackbarFunc({
        snackbarMessage: t("jobs.similar_jobs.job_unsaved"),
        severity: "success",
      });
    } else {
      let params = {
        jobId: id,
        canCancel: false,
        origin: "similar-job-ijp",
        appliedDialog: false,
      };
      const bookmarkRes = await dispatch(bookmarkJob(params));
      setSavingJob(false);
      setSavedJob(bookmarkRes?.payload?.bookmarkJob?.bookmark);
      triggerSnackbarFunc({
        snackbarMessage: t("jobs.similar_jobs.job_saved"),
        severity: "success",
      });
    }
  }

  return (
    <IconButtonStyled
      aria-label="similar-job-bookmark-button"
      onClick={handleBookmarkJob}
      disabled={savingJob}
    >
      {savedJob ? <FilledBookmarkIconStyled /> : <BookmarkIconStyled />}
    </IconButtonStyled>
  );
}
